// Куда идти после вердикта — и сам ли ученик туда идёт.
//
// Карточка стоит под вердиктом на экране задачи. Три случая: следующая задача
// набора, близнец того же навыка (его даёт сервер на ошибке, пока она ещё в
// голове) и пустой набор.
//
// **Условия следующей задачи здесь нет** (14 сентября 2026). Оно стояло прямо
// в карточке и портило обе секунды разом: под вердиктом лежит разбор только
// что решённой задачи, а глаз уводило на новое условие — читать его там всё
// равно нельзя, считать по нему негде, и решение о следующей задаче ученик
// принимал, уже увидев её. Кнопка «Далее» ведёт в задачу, а не показывает её.
// Заодно ушла подписка `today.item` по ключу следующей задачи — лишний вызов
// функции на каждый вердикт.
//
// **Обратного отсчёта здесь нет** (14 сентября 2026). Пять секунд до
// автоматического перехода жили на верном ответе с 11 сентября и держались на
// том, что читать после него нечего: на экране оставалась строчка «Ответ 14».
// В тот же день под верным ответом встал разбор, и отсчёт стал увозить с
// середины чтения — первым лечением было гасить его на развёрнутом разборе,
// вторым и окончательным убрать совсем. Страницу листает ученик, а не таймер:
// дочитал — нажал «Далее».

import { useState } from "react";
import { api, useMutation } from "../api";
import { IconArrow } from "./Icons";

export function Next({
  learnerKey,
  forward,
  twin,
  prev,
  onSolve,
  onOpenDay,
  onToday,
}: {
  learnerKey: string;
  /** Задача, куда ведёт «Далее». `null` — в наборе не осталось незакрытых. */
  forward: string | null;
  /** `forward` — близнец того же навыка, а не следующая задача набора. */
  twin: boolean;
  /** Предыдущая задача набора; `null` на первой. */
  prev: string | null;
  onSolve: (itemKey: string) => void;
  onOpenDay: () => void;
  onToday: () => void;
}) {
  const more = useMutation(api.today.more);
  const [drained, setDrained] = useState(false);

  // Добавка прямо здесь, а не только на наборе: конец набора застаёт ученика
  // на этом экране, и отправлять его на «Сегодня» ради одной кнопки значит
  // рвать занятие ровно в той точке, где он готов продолжать.
  const takeMore = () => {
    setDrained(false);
    void more({ learnerKey }).then((r) => {
      if (r.firstItemKey === null) setDrained(true);
      else onSolve(r.firstItemKey);
    });
  };

  if (forward === null) {
    return (
      <div className="card card--act">
        <div className="card__label card__label--act">Набор закрыт</div>
        <div className="muted" style={{ marginTop: 10, fontSize: 15 }}>
          {drained
            ? "Задачи по открытым навыкам на сегодня кончились. Дальше — новый навык или своя тема."
            : "Набор пройден. Можно закрыть день движением навыков, взять ещё порцию повторений, открыть новый навык или решать свою тему — минуты набора это оценка, а не норма."}
        </div>
        <div className="row row--actions" style={{ marginTop: 18 }}>
          <button className="btn btn--primary" onClick={takeMore} disabled={drained}>
            Ещё задачи
          </button>
          <button className="btn btn--quiet" onClick={onOpenDay}>
            Итог дня
          </button>
          {prev !== null ? (
            <button className="link link--quiet" onClick={() => onSolve(prev)}>
              Назад
            </button>
          ) : null}
          {/* Три пути дальше, а не один: порция повторений — кнопкой здесь,
              новый навык и своя тема — на экране набора, где стоят их
              карточки. Подпись называет, что там, а не «К набору»: пройденный
              набор этим словом звал обратно в пустоту (25 сентября 2026). */}
          <button className="link link--quiet" onClick={onToday}>
            Новый навык · своя тема
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card card--act">
      <div className="card__label card__label--act">
        <IconArrow size={16} color="oklch(0.45 0.13 255)" />
        {twin ? "Дальше — похожая задача" : "Дальше — следующая задача набора"}
      </div>
      <div className="row row--actions" style={{ marginTop: 18 }}>
        <button className="btn btn--primary" onClick={() => onSolve(forward)}>
          Далее
        </button>
        {prev !== null ? (
          <button className="link link--quiet" onClick={() => onSolve(prev)}>
            Назад
          </button>
        ) : null}
        <button className="link link--quiet" onClick={onToday}>
          К набору
        </button>
      </div>
    </div>
  );
}

/**
 * Шаг по закрытым задачам набора: пересмотр, а не решение.
 *
 * Отвечать здесь нельзя, и кнопок вперёд-назад достаточно: ученик пришёл
 * посмотреть, чем кончилась задача, а не решать её второй раз.
 */
export function ReviewNav({
  prev,
  next,
  onSolve,
  onToday,
}: {
  prev: string | null;
  next: string | null;
  onSolve: (itemKey: string) => void;
  onToday: () => void;
}) {
  return (
    <div className="card">
      <div className="card__label">Закрытая задача</div>
      <div className="muted" style={{ marginTop: 10, fontSize: 15 }}>
        Ответ уже засчитан: задача открыта записью, а не заново. Второй раз одна
        задача не отвечается — в журнале появилась бы попытка, которой не было.
      </div>
      <div className="row row--actions" style={{ marginTop: 18 }}>
        <button
          className="btn btn--quiet"
          disabled={prev === null}
          onClick={() => prev !== null && onSolve(prev)}
        >
          Назад
        </button>
        <button
          className="btn btn--quiet"
          disabled={next === null}
          onClick={() => next !== null && onSolve(next)}
        >
          Далее
        </button>
        <button className="link link--quiet" onClick={onToday}>
          К набору
        </button>
      </div>
    </div>
  );
}
